import { type ComponentType, type ReactNode, useEffect } from "react";
import Card from "~/common/components/Card";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { MarketingConsentCheckbox } from "~/features/registration/components/MediasRegistrationBanner/MarketingConsentCheckbox";
import { Box, styled, type SvgIconProps } from "~/common/mui";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import WbIncandescentIcon from "@mui/icons-material/WbIncandescent";
import ScreenNewsIcon from "~/common/mui/icons/ScreenNews";

interface Props {
  className?: string;
  selected: boolean;
  setSelected: (val: boolean) => void;
}

export const MarketingConsent = ({ selected, setSelected, className }: Props) => {
  const { t } = useTranslation();

  return (
    <MarketingConsent.Card className={className}>
      <MarketingConsent.Title>{t("newsletter.consent.title")}</MarketingConsent.Title>
      <MarketingConsent.ConsentCheckbox selected={selected} setSelected={setSelected} />
      <MarketingConsent.SectionTitle>{t("newsletter.reason.title")}</MarketingConsent.SectionTitle>
      <MarketingConsent.Reasons reasons={DEFAULT_REASONS} />
    </MarketingConsent.Card>
  );
};

MarketingConsent.Card = Card;

const DEFAULT_REASONS = [
  {
    icon: EmojiEventsIcon,
    children: <Trans i18nKey={"newsletter.reason.1"} />,
  },
  {
    icon: ScreenNewsIcon,
    children: <Trans i18nKey={"newsletter.reason.2"} />,
  },
  {
    icon: LocalOfferIcon,
    children: <Trans i18nKey={"newsletter.reason.3"} />,
  },
  {
    icon: LocalActivityIcon,
    children: <Trans i18nKey={"newsletter.reason.4"} />,
  },
  {
    icon: WbIncandescentIcon,
    children: <Trans i18nKey={"newsletter.reason.5"} />,
  },
];

interface ConsentCheckboxProps {
  className?: string;
  selected?: boolean;
  setSelected?: (val: boolean) => void;
  mandatory?: boolean;
  hideTitle?: boolean;
  disabled?: boolean;
}

const ConsentCheckbox = ({ selected, setSelected, disabled, hideTitle, className }: ConsentCheckboxProps) => {
  const renderText = (suffix: string) => <Trans i18nKey={`newsletter.consent.first.${suffix}`} />;

  useEffect(() => {
    const nodes = Array.prototype.slice.call(document.querySelectorAll(".js__newsletter a:not([href])"));
    nodes.forEach((node: HTMLAnchorElement) => {
      node.href = `mailto:${node.text}`;
    });
  });

  return (
    <ConsentItemRoot className={classNames("js__newsletter", className)} data-disabled={disabled || undefined}>
      {!hideTitle && <ConsentItemSubtitle>{renderText("title")}</ConsentItemSubtitle>}
      <MarketingConsentCheckbox
        data-test-id={"newsletter-consent-checkbox"}
        checked={selected}
        onCheck={setSelected}
        centerCheckbox={false}
        disabled={disabled}
      />
    </ConsentItemRoot>
  );
};

MarketingConsent.ConsentCheckbox = ConsentCheckbox;

MarketingConsent.Title = styled("p")(({ theme }) => ({
  ...getFontStyle(FontSize.TitleLarge),
  marginBottom: theme.spacing(3),
}));

MarketingConsent.SectionTitle = styled(MarketingConsent.Title)(({ theme }) => ({
  marginTop: theme.spacing(3.75),
}));

const ConsentItemSubtitle = styled("p")(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const ConsentItemRoot = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(1),
  [`&[data-disabled] p`]: {
    color: theme.palette.text.disabled,
  },
}));

const ReasonRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ReasonIconCircle = styled(Box)(({ theme }) => ({
  width: theme.spacing(10),
  height: theme.spacing(10),
  marginBottom: theme.spacing(2),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
}));

interface ReasonsProps {
  className?: string;
  reasons: ReasonProps[];
}

MarketingConsent.Reasons = styled(({ reasons, className }: ReasonsProps) => {
  return (
    <div className={className}>
      {reasons.map((reason, index) => (
        <Reason key={index} icon={reason.icon}>
          {reason.children}
        </Reason>
      ))}
    </div>
  );
})(({ theme }) => ({
  backgroundColor: theme.palette.surface.main,
  display: "flex",
  flexFlow: "column",
  padding: theme.spacing(1),
  gap: theme.spacing(4),
  [theme.breakpoints.up("tablet")]: {
    padding: theme.spacing(4),
    flexFlow: "row",
    "> *": {
      flex: "1",
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    paddingInline: theme.spacing(8),
  },
}));

interface ReasonProps {
  icon: ComponentType<SvgIconProps>;
  children?: ReactNode;
}
const Reason = ({ icon: ReasonIcon, children }: ReasonProps) => (
  <ReasonRoot>
    <ReasonIconCircle>
      <ReasonIcon sx={{ fontSize: 30, color: "primary.main" }} />
    </ReasonIconCircle>
    <Box sx={{ textAlign: "center" }}>{children}</Box>
  </ReasonRoot>
);
